import getBasicImageProps from '@/components/atoms/Image/helpers/get-basic-image-props'
import buildImageUrl from '@/components/atoms/Image/helpers/build-url'
import { ImageOptionsTypes } from '@/components/atoms/Image/types'
import { isWebP } from './helpers'
import { defaultFluidOptions, breakpoints } from './defaults'

function getImageProps(image: ImageTypes | string, args: ImageOptionsTypes = {}): GatsbyImageStoryblokProps | null {
  const imageProps = getBasicImageProps(image)

  if (!imageProps) {
    return null
  }

  const options = {
    ...defaultFluidOptions,
    ...args
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { maxWidth, base64, useBase64 } = options

  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    metadata: { dimensions, lqip },
    originalPath
  } = imageProps

  let desiredAspectRatio = dimensions.aspectRatio

  // If we're cropping, calculate the specified aspect ratio
  if (options.maxHeight && maxWidth) {
    desiredAspectRatio = maxWidth / options.maxHeight
  }

  const maxHeight = maxWidth && (options.maxHeight || Math.round(maxWidth / dimensions.aspectRatio))

  let forceConvert: string | null = null
  if (options.toFormat) {
    forceConvert = options.toFormat
  } else if (isWebP(originalPath)) {
    forceConvert = 'jpg'
  }

  // @ts-ignore
  const widths = breakpoints.filter((width) => width < maxWidth && width < dimensions.width).concat(dimensions.width)
  const defaultSizes = breakpoints.map((size) => `(max-width: ${size}px) ${size}px`).join(', ')
  // @ts-ignore
  const sizes = options?.sizes || `${defaultSizes}, ${maxWidth}px`
  const initial: { webp: string[]; base: string[] } = { webp: [], base: [] }
  const filteredWidths = widths.length === 1 ? widths : widths.filter((currentWidth) => currentWidth < dimensions.width)
  const srcSets = filteredWidths.reduce((acc: { webp: string[]; base: string[] }, currentWidth) => {
    const currentHeight = Math.round(currentWidth / desiredAspectRatio)

    const size = {
      width: currentWidth,
      height: currentHeight
    }

    const webpUrl = buildImageUrl(originalPath, {
      ...options,
      ...size,
      ...{ format: 'webp' }
    })

    const baseUrl = buildImageUrl(originalPath, {
      ...options,
      ...size,
      ...{ format: forceConvert }
    })

    acc.webp.push(`${webpUrl} ${currentWidth}w`)
    acc.base.push(`${baseUrl} ${currentWidth}w`)
    return acc
  }, initial)

  const imgSize = { width: maxWidth, height: maxHeight }

  const src = buildImageUrl(originalPath, {
    ...options,
    ...imgSize,
    ...{ format: forceConvert }
  })

  const srcWebp = buildImageUrl(originalPath, {
    ...options,
    ...imgSize,
    ...{ format: 'webp' }
  })

  const srcPath = src
  const srcSetPath = srcSets.base.join(',\n') || null
  const srcWebpPath = srcWebp
  const srcSetWebpPath = srcSets.webp.join(',\n') || null

  return {
    base64: (typeof image !== 'string' && image?.base64) || null,
    aspectRatio: desiredAspectRatio,
    src: srcPath,
    srcSet: srcSetPath,
    srcWebp: srcWebpPath,
    srcSetWebp: srcSetWebpPath,
    sizes,
    width: imgSize.width!,
    height: imgSize.height!
  }
}

export default getImageProps
